import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../../music24/components/primitives/Panel';
import { Tag } from '../../../music24/components/primitives/Tag';
import { mediaBlockLabel, mediaBlockMainOption } from '../../widgets/views/mediaWidget/mediaBlockLabel';
import { mediaStatsMessages } from './mediaStatsMessages';
import { MediaStatsRow } from './mediaStatsRows';
import { SMLMediaDayChart } from './SMLMediaDayChart';
import { SMLMediaFunnel } from './SMLMediaFunnel';
import { SMLMediaNumbers } from './SMLMediaNumbers';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media panel; each part uses its own slice of it */
import styles from './SMLMediaPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLMediaBlockBoxProps {
  row: MediaStatsRow;
  pageViews: number;
}

/** The numbers of one Media block (design c1, c3): the name, the numbers, the funnel and the days. */
const SMLMediaBlockBoxComponent: React.FC<SMLMediaBlockBoxProps> = ({ row, pageViews }) => {
  const intl = useIntl();
  const data = row.widget?.data ?? null;
  // A removed block has no file now; its numbers tell what it was: plays come only from a video.
  const kind = data?.media?.kind ?? (row.item.plays > 0 ? 'video' : 'image');
  const title = data === null ? intl.formatMessage(mediaStatsMessages.title) : mediaBlockLabel(intl, data);
  const facts = [
    intl.formatMessage(kind === 'video' ? mediaStatsMessages.kindVideo : mediaStatsMessages.kindImage),
    row.position === null ? null : intl.formatMessage(mediaStatsMessages.position, { position: row.position }),
    data === null ? null : mediaBlockMainOption(intl, data),
  ].filter((part): part is string => part !== null);
  const isEmpty = row.item.seen === 0 && row.item.plays === 0 && row.item.clicks === 0;

  return (
    <Panel nested className={styles.box} data-testid={`media-box-${row.blockUUID}`}>
      <div className={styles.boxHead}>
        <span className={styles.boxTitle}>{title}</span>
        {row.widget === null && <Tag>{intl.formatMessage(mediaStatsMessages.removed)}</Tag>}
      </div>
      <span className={styles.boxFacts}>{facts.join(' · ')}</span>
      {isEmpty ? (
        <div className={styles.boxEmpty} data-testid="media-box-empty">
          <p>{intl.formatMessage(kind === 'video' ? mediaStatsMessages.emptyVideo : mediaStatsMessages.emptyImage)}</p>
          <p className={styles.numberHint}>{intl.formatMessage(mediaStatsMessages.emptyNote)}</p>
        </div>
      ) : (
        <>
          <SMLMediaNumbers
            item={row.item}
            kind={kind}
            pageViews={pageViews}
            durationSec={data?.media?.durationSec ?? null}
          />
          {kind === 'video' && <SMLMediaFunnel item={row.item} />}
          {row.item.series.length > 0 && <SMLMediaDayChart series={row.item.series} canShowPlays={kind === 'video'} />}
        </>
      )}
    </Panel>
  );
};

export const SMLMediaBlockBox = TypedMemo(SMLMediaBlockBoxComponent);
